import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { DISCIPLINE_KEYS } from "@/lib/disciplines";

export default function Releases() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    name: "", artizen_id: "", discipline: "music", project_name: "",
    description: "", work_link: "", contact_email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/releases/published").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, []);

  const wordCount = form.description.trim().split(/\s+/).filter(Boolean).length;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact_email || !form.project_name || !form.description || !form.work_link) {
      toast.error("All fields except Artizen ID are required.");
      return;
    }
    if (wordCount > 150) {
      toast.error("Description must be 150 words or fewer.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/releases/apply", form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="THE RELEASE" />
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-16">
          <SectionLabel>The Release</SectionLabel>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[7rem] font-bold leading-[0.9] tracking-tighter">
            A label. A publisher. <br /> <span className="hl-coral">A point of view.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-white/85 text-base md:text-lg leading-relaxed">
            anArtizen curates and releases work across music, film, and other disciplines. Released work carries editorial backing, distribution support, and the anArtizen network behind it.
          </p>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
          <SectionLabel>What we're looking for</SectionLabel>
          <p className="font-display text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight max-w-4xl">
            Work that has a <span className="hl-cyan">point of view</span>. Work made by people who have <span className="hl-coral">already done the work</span>. Not portfolios — projects ready to meet an audience.
          </p>
          <p className="mt-8 max-w-2xl text-white/70 text-base leading-relaxed">
            We release a small number of projects a year. Selection is editorial. We are not looking for the safest thing in the room.
          </p>
        </div>
      </section>

      <section className="border-b border-border bg-[#0c0c0e]">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16">
          <SectionLabel>Current & recent releases</SectionLabel>
          {items.length === 0 ? (
            <div className="border border-dashed border-border p-16 text-center">
              <p className="font-display text-3xl md:text-4xl font-bold text-white/40">Releases coming soon.</p>
              <p className="meta-mono mt-4">— THIS SECTION POPULATES AS WE RELEASE WORK</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-px bg-border">
              {items.map((r) => (
                <div key={r.release_id} className="bg-background p-6">
                  <p className="meta-mono !text-primary">— {r.discipline?.toUpperCase()}</p>
                  <h3 className="font-display text-2xl font-bold mt-3 leading-tight">{r.project_name}</h3>
                  <p className="mt-2 text-white/70 text-sm">{r.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-16">
          <SectionLabel>Submit to The Release</SectionLabel>
          {done ? (
            <div className="border border-border p-10" data-testid="release-success">
              <p className="font-display text-4xl md:text-5xl font-bold leading-tight">Submission received.</p>
              <p className="meta-mono mt-4 !text-white/65">— WE REVIEW EVERY SUBMISSION. WE RESPOND ONLY TO SHORTLISTED APPLICATIONS.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-5" data-testid="release-form">
              <FormField label="Name" testid="release-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <FormField label="Artizen ID (optional · members only)" testid="release-azid" value={form.artizen_id} onChange={(v) => setForm({ ...form, artizen_id: v })} placeholder="ANZ-0001-…" />
              <FormSelect label="Discipline" testid="release-discipline" value={form.discipline} onChange={(v) => setForm({ ...form, discipline: v })} options={DISCIPLINE_KEYS} />
              <FormField label="Project name" testid="release-project" value={form.project_name} onChange={(v) => setForm({ ...form, project_name: v })} />
              <div>
                <p className="meta-mono mb-2">— SHORT DESCRIPTION ({wordCount}/150 WORDS)</p>
                <textarea data-testid="release-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} className="w-full bg-transparent border border-border p-4 outline-none focus:border-primary text-sm" placeholder="What is this work, and why now?" />
              </div>
              <FormField label="Link to work (private links acceptable)" testid="release-link" value={form.work_link} onChange={(v) => setForm({ ...form, work_link: v })} placeholder="https://" />
              <FormField label="Contact email" testid="release-email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} type="email" />
              <button disabled={submitting} data-testid="release-submit" className="btn-pill btn-coral mt-2">
                {submitting ? "Sending…" : <>Submit to The Release <ArrowRight size={14} /></>}
              </button>
              <p className="meta-mono mt-4 !text-white/45">— WE REVIEW EVERY SUBMISSION. WE RESPOND ONLY TO SHORTLISTED APPLICATIONS.</p>
            </form>
          )}
        </div>
      </section>
    </PageShell>
  );
}

function FormField({ label, testid, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div>
      <p className="meta-mono mb-2">— {label.toUpperCase()}</p>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-transparent border border-border p-3 outline-none focus:border-primary text-sm" />
    </div>
  );
}
function FormSelect({ label, testid, value, onChange, options }) {
  return (
    <div>
      <p className="meta-mono mb-2">— {label.toUpperCase()}</p>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-background border border-border p-3 outline-none focus:border-primary text-sm">
        {options.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
      </select>
    </div>
  );
}
